import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization } from './_finHelpers';

export default function BoatLoanCalc() {
  const [tab, setTab] = useState('price');
  return (
    <CalcShell look="cnet" title="Boat Loan Calculator" subtitle="Estimate monthly payments and total cost for financing a boat purchase."
      about={<>
        <p>A <b>boat loan</b> is used to finance the purchase of a boat. Depending on price and creditor, terms often range from 5 to 20 years. Sales tax and title/registration fees can add substantially to the total cost.</p>
      </>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={()=>setTab('price')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='price'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Total Price</button>
        <button onClick={()=>setTab('monthly')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='monthly'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Monthly Payment</button>
      </div>
      {tab === 'price' ? <TotalPrice/> : <MonthlyPay/>}
    </CalcShell>
  );
}

function TotalPrice() {
  const [price, setPrice] = useState(35000);
  const [term, setTerm] = useState(10);
  const [rate, setRate] = useState(7);
  const [down, setDown] = useState(7000);
  const [downMode, setDownMode] = useState('$');
  const [tradeIn, setTradeIn] = useState(0);
  const [salesTax, setSalesTax] = useState(3);
  const [fees, setFees] = useState(2800);
  const [includeInLoan, setIncludeInLoan] = useState(false);

  const c = useMemo(() => {
    const downAmt = downMode === '%' ? price * down/100 : down;
    const taxAmt = Math.max(0, price - tradeIn) * salesTax/100;
    const loan = price - downAmt - tradeIn + (includeInLoan ? taxAmt + fees : 0);
    const n = term * 12;
    const amort = buildAmortization(loan, rate, n);
    const upfront = downAmt + (includeInLoan ? 0 : taxAmt + fees);
    const totalCost = amort.totalPaid + upfront + tradeIn * 0;
    return { downAmt, taxAmt, loan, amort, upfront, totalCost };
  }, [price, term, rate, down, downMode, tradeIn, salesTax, fees, includeInLoan]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Boat Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={price} onChange={e=>setPrice(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Loan Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><select defaultValue="years"><option>years</option></select></div></div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Down Payment</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={down} onChange={e=>setDown(+e.target.value||0)}/>
              <select value={downMode} onChange={e=>setDownMode(e.target.value)}><option value="$">$</option><option value="%">%</option></select>
            </div>
          </div>
          <div className="field"><label>Trade-in Value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={tradeIn} onChange={e=>setTradeIn(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Sales Tax</label><div className="flex gap-2"><input type="number" step="0.01" value={salesTax} onChange={e=>setSalesTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Fees</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={fees} onChange={e=>setFees(+e.target.value||0)} className="flex-1"/></div></div>
          <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={includeInLoan} onChange={e=>setIncludeInLoan(e.target.checked)}/> Include all fees in loan</label>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Monthly Pay</div><div className="result-value" data-testid="boat-monthly">{fmt(c.amort.M)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total Loan Amount</td><td className="text-right font-semibold">{fmt(c.loan)}</td></tr>
            <tr><td>Sale Tax</td><td className="text-right">{fmt(c.taxAmt)}</td></tr>
            <tr><td>Upfront Payment</td><td className="text-right">{fmt(c.upfront)}</td></tr>
            <tr><td>Total of {term*12} Loan Payments</td><td className="text-right">{fmt(c.amort.totalPaid)}</td></tr>
            <tr><td>Total Loan Interest</td><td className="text-right text-orange-600">{fmt(c.amort.totalInterest)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Total Cost (price, interest, tax, fees)</td><td className="text-right">{fmt(c.totalCost)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Principal',value:c.loan,color:'#2563eb'},{label:'Interest',value:c.amort.totalInterest,color:'#10b981'}]} size={170}/></div>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c.amort}/></div>
        <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.amort.yearly} startBalance={c.loan}/></div>
      </div>
    </>
  );
}

function MonthlyPay() {
  const [monthly, setMonthly] = useState(350);
  const [term, setTerm] = useState(10);
  const [rate, setRate] = useState(7);
  const [down, setDown] = useState(7000);
  const [downMode, setDownMode] = useState('$');
  const [tradeIn, setTradeIn] = useState(0);
  const [salesTax, setSalesTax] = useState(3);
  const [fees, setFees] = useState(2800);
  const [includeInLoan, setIncludeInLoan] = useState(false);
  const c = useMemo(() => {
    const r = rate/100/12, n = term*12;
    const loan = r === 0 ? monthly * n : monthly * (1 - Math.pow(1+r, -n))/r;
    // Reverse-engineer boat price: same math as Auto Loan
    const t = salesTax/100;
    const downFixed = downMode === '%' ? 0 : down;
    const downRate = downMode === '%' ? down/100 : 0;
    // Equation: loan + downFixed + downRate*price + tradeIn = price + (includeInLoan ? tax + fees : 0)
    // tax = (price - tradeIn) * t
    let price;
    if (includeInLoan) {
      // loan + down + tradeIn = price * (1 - downRate) + (price - tradeIn) * t + fees
      // loan + downFixed + tradeIn + tradeIn*t - fees = price * (1 - downRate + t)
      const num = loan + downFixed + tradeIn + tradeIn * t - fees;
      const denom = 1 - downRate + t;
      price = denom !== 0 ? num / denom : 0;
    } else {
      const num = loan + downFixed + tradeIn;
      const denom = 1 - downRate;
      price = denom !== 0 ? num / denom : 0;
    }
    const downAmt = downMode === '%' ? price * down/100 : down;
    const taxAmt = Math.max(0, price - tradeIn) * t;
    const upfront = downAmt + (includeInLoan ? 0 : taxAmt + fees);
    const totalPaid = monthly * n;
    const interest = totalPaid - loan;
    const amort = loan > 0 ? buildAmortization(loan, rate, n) : null;
    const totalCost = totalPaid + upfront;
    return { loan, price, downAmt, taxAmt, upfront, total: totalPaid, interest, amort, totalCost };
  }, [monthly, term, rate, down, downMode, tradeIn, salesTax, fees, includeInLoan]);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div data-print="inputs">
          <div className="field"><label>Monthly Pay</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthly} onChange={e=>setMonthly(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Loan Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Down Payment</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={down} onChange={e=>setDown(+e.target.value||0)}/>
              <select value={downMode} onChange={e=>setDownMode(e.target.value)}><option value="$">$</option><option value="%">%</option></select>
            </div>
          </div>
          <div className="field"><label>Trade-in Value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={tradeIn} onChange={e=>setTradeIn(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Sales Tax</label><div className="flex gap-2"><input type="number" step="0.01" value={salesTax} onChange={e=>setSalesTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Fees</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={fees} onChange={e=>setFees(+e.target.value||0)} className="flex-1"/></div></div>
          <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={includeInLoan} onChange={e=>setIncludeInLoan(e.target.checked)}/> Include all fees in loan</label>
        </div>
        <div data-print="result">
          <div className="result-panel mb-4"><div className="result-title">You Can Afford a Boat Worth</div><div className="result-value" data-testid="boat-mp-price">{fmt(Math.max(0, c.price))}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total Loan Amount</td><td className="text-right font-semibold">{fmt(c.loan)}</td></tr>
            <tr><td>Sale Tax</td><td className="text-right">{fmt(c.taxAmt)}</td></tr>
            <tr><td>Upfront Payment</td><td className="text-right">{fmt(c.upfront)}</td></tr>
            <tr><td>Total of {term*12} Loan Payments</td><td className="text-right">{fmt(c.total)}</td></tr>
            <tr><td>Total Loan Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Total Cost</td><td className="text-right">{fmt(c.totalCost)}</td></tr>
          </tbody></table>
          <div className="mt-4">
            <PieChart parts={[
              { label: 'Principal', value: c.loan,     color: '#2563eb' },
              { label: 'Interest',  value: c.interest, color: '#10b981' },
            ]} size={170}/>
          </div>
        </div>
      </div>
      {c.amort && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-print="tables">
          <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c.amort}/></div>
          <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.amort.yearly} startBalance={c.loan}/></div>
        </div>
      )}
    </>
  );
}
